<!--
  PreviewSeparator (Spec 118 E6/E7) — draggable / keyboard-resizable boundary left of a preview
  pane. `width` is the pane width in px, remembered per browser under `storageKey`.
  Arrow left widens the pane (the separator sits on its left edge), arrow right narrows it.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';

  export let width: number;
  export let storageKey: string;
  export let defaultWidth = 500;
  export let testId: string;
  /** Id of the pane this separator resizes (aria-controls). */
  export let controls = '';
  /** Breakpoint from which the pane is resizable, e.g. 'xl:block' (hidden below: stacked layout). */
  export let showClass = 'xl:block';

  const MIN = 360;
  const STEP = 24;
  let el: HTMLElement;
  // The limit follows the layout container (parent), not the window: sidebars etc. take space.
  const maxWidth = () => Math.max(MIN, Math.round((el?.parentElement?.clientWidth ?? 0) * 0.6));
  const clamp = (value: number) => Math.min(maxWidth(), Math.max(MIN, Math.round(value)));

  let max = MIN;

  function readStored(): number {
    try {
      const stored = Number(localStorage.getItem(storageKey));
      return Number.isFinite(stored) && stored > 0 ? stored : defaultWidth;
    } catch {
      return defaultWidth;
    }
  }

  function persist() {
    try {
      localStorage.setItem(storageKey, String(width));
    } catch {
      /* no storage: width only lives for this visit */
    }
  }

  function setWidth(value: number) {
    width = clamp(value);
    max = maxWidth();
  }

  onMount(() => {
    setWidth(readStored());
  });

  function reclamp() {
    setWidth(width);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowLeft') setWidth(width + STEP);
    else if (e.key === 'ArrowRight') setWidth(width - STEP);
    else return;
    persist();
    e.preventDefault();
  }

  let drag: { startX: number; startWidth: number } | null = null;

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0 || !e.isPrimary) return;
    drag = { startX: e.clientX, startWidth: width };
    // Capture keeps move/up events on this element, so no window listeners need cleanup.
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }

  function onPointerMove(e: PointerEvent) {
    if (drag) setWidth(drag.startWidth + (drag.startX - e.clientX));
  }

  function onPointerEnd(e: PointerEvent) {
    if (!drag) return;
    drag = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    persist();
  }
</script>

<svelte:window on:resize={reclamp} />

<!-- A focusable separator is an interactive widget (WAI-ARIA window splitter). -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
<div
  role="separator"
  aria-orientation="vertical"
  tabindex="0"
  aria-valuenow={width}
  aria-valuemin={MIN}
  aria-valuemax={max}
  aria-label={$_('components.document_preview.resize')}
  aria-controls={controls || undefined}
  bind:this={el}
  data-testid={testId}
  class="hidden {showClass} w-1.5 shrink-0 cursor-col-resize bg-transparent hover:bg-accent/40 focus-visible:bg-accent/60 outline-none touch-none"
  on:keydown={onKeyDown}
  on:pointerdown={onPointerDown}
  on:pointermove={onPointerMove}
  on:pointerup={onPointerEnd}
  on:pointercancel={onPointerEnd}
></div>
